import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, withTheme } from '@material-ui/core/styles';
import { Paper, Typography, Grid } from '@material-ui/core';
import { Tag } from '@ds/react-components';
import moment from 'moment';
import { Initial } from 'common/initial/Initial';
import { truncateText } from 'utils/misc';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

const useStyles = makeStyles((theme) => ({
  root: (props) => ({
    width: 350,
    height: 190,
    padding: theme.spacing(2),
    position: 'relative',
    cursor: props.disabled ? 'inherit' : 'pointer',
    '&:hover': {
      border: props.disabled
        ? 'none'
        : `1px solid ${theme.palette.common.primary}`,
    },
  }),
  line: (props) => ({
    position: 'absolute',
    left: 0,
    top: theme.spacing(3),
    width: 4,
    height: 30,
    backgroundColor: props.color,
  }),
  accountDetails: (props) => ({
    marginTop: theme.spacing(1),
    '& p': {
      color: theme.palette.common.lightText,
    },
    '& hr': {
      border: `0.5px solid #e6eaee`,
    },
  }),
  nameStatus: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  text: (props) => ({
    color: props.disabled
      ? theme.palette.common.lightText
      : theme.palette.common.black,
  }),
  cancelReasonDisabled: (props) => ({
    color: props.disabled
      ? theme.palette.common.lightText
      : theme.palette.common.error,
  }),
  icon: {
    color: theme.palette.common.error,
    marginRight: '2px',
    marginLeft: '-2px',
    fontSize: 18,
  },
  cancelSection: {
    marginTop: theme.spacing(0.5),
  },
  cancelReason: {
    color: theme.palette.common.error,
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  cancelMessage: {
    color: theme.palette.common.error,
  },
}));

const AccountCard = (props) => {
  const classes = useStyles(props);
  const { theme, account, dateFormat, status, onAccountClick } = props;
  if (!account) return null;

  const dob = moment(account.dob).format(dateFormat);

  const initials = account.firstName
    ? account.firstName.substring(0, 1)
    : '' + account.lastName
    ? account.lastName.substring(0, 1)
    : '';

  const cancelReason =
    account.cancelReason === 'Prep For Debt Col'
      ? 'Pending debt collection'
      : account.cancelReason === 'Pending Write Off'
      ? 'Pending write off'
      : null;

  const cancelMessage =
    account.cancelReason === 'Prep For Debt Col'
      ? 'Account is due to be sent to debt collection if overdue is not paid'
      : account.cancelReason === 'Pending Write Off'
      ? 'Account may be sent to debt collection if overdue is not paid'
      : null;

  return (
    <Paper
      className={classes.root}
      style={cancelReason !== null ? { height: 223 } : null}
      onClick={props.disabled ? null : () => onAccountClick(account.adfitNo)}>
      <div className={classes.line}></div>
      <Grid container>
        <Grid item xs={2}>
          <Initial
            size={46}
            variant={'h5'}
            text={initials}
            backgroundColor={
              props.disabled ? theme.palette.common.lightText : props.color
            }
          />
        </Grid>
        <Grid item xs={10}>
          <div>
            <div className={classes.nameStatus}>
              <Typography variant="h5" className={classes.text}>
                {truncateText(account.firstName + ' ' + account.lastName, 20)}
              </Typography>
              <Tag
                label={status}
                type={
                  status === 'Active'
                    ? 'success'
                    : status === 'Suspended'
                    ? 'warning'
                    : 'secondary'
                }
              />
            </div>

            <div>
              <Typography className={classes.text}>
                Date of birth: {dob}
              </Typography>
            </div>
            <div className={classes.accountDetails}>
              <div>
                <Typography>
                  Debitsuccess ID: {account && account.adfitNo}
                </Typography>
              </div>
              <div>
                <Typography>
                  {truncateText(account.facilityName, 30)}
                </Typography>
              </div>
              <hr />
              <div>
                <Typography>
                  {truncateText(account.facilityAccountName, 30)}
                </Typography>
              </div>
            </div>
            {cancelReason !== null && (
              <div className={classes.cancelSection}>
                <Grid container alignItems="center">
                  <ErrorOutlineIcon
                    className={`${classes.icon} ${classes.cancelReasonDisabled}`}
                  />
                  <Typography
                    className={`${classes.cancelReason} ${classes.cancelReasonDisabled}`}>
                    {cancelReason}
                  </Typography>
                </Grid>
                <Typography
                  className={`${classes.cancelMessage} ${classes.cancelReasonDisabled}`}>
                  {cancelMessage}
                </Typography>
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    </Paper>
  );
};

AccountCard.propTypes = {
  account: PropTypes.object,
  dateFormat: PropTypes.string,
  status: PropTypes.string,
  disabled: PropTypes.bool,
  color: PropTypes.string,
  onAccountClick: PropTypes.func,
};

export default withTheme(AccountCard);
